document.addEventListener('DOMContentLoaded', () => {
  const root = document.querySelector('.product-reviews');
  if (!root) return;

  const { apiUrl, productId, perPage, showSamples, samplePhoto } = root.dataset;
  if (!apiUrl) {
    root.hidden = true;
    return;
  }

  // Design-preview fixtures, rendered through the real review card pipeline; votes/reports disabled on them.
  const samplesEnabled = showSamples === 'true';
  const SAMPLE_REVIEWS = [
    {
      _sample: true,
      id: 'sample-full',
      reviewer_name: 'Sample: Every Field',
      verified: true,
      rating: 4,
      title: 'A review using every available field',
      body: 'This sample shows the maximum layout: verified badge, title, fit answer, height, body type, photos, and votes. Use it to check spacing and alignment against the design.',
      size_fit: 'Runs small',
      height: "5'9\"–5'11\"",
      body_type: 'Athletic',
      pictures: samplePhoto ? [{ compact: samplePhoto }, { compact: samplePhoto }] : [],
      helpful_up: 12,
      helpful_down: 1,
    },
    {
      _sample: true,
      id: 'sample-minimal',
      reviewer_name: 'Sample: Minimal',
      verified: false,
      rating: 5,
      title: '',
      body: 'Minimal case: rating, name and body only — exactly how legacy reviews (written before the custom questions existed) render.',
      size_fit: null,
      height: null,
      body_type: null,
      pictures: [],
      helpful_up: 0,
      helpful_down: 0,
    },
  ];

  const els = {
    loading:     root.querySelector('[data-reviews-loading]'),
    error:       root.querySelector('[data-reviews-error]'),
    empty:       root.querySelector('[data-reviews-empty]'),
    noMatch:     root.querySelector('[data-reviews-no-match]'),
    content:     root.querySelector('[data-reviews-content]'),
    avgRating:   root.querySelector('[data-avg-rating]'),
    avgStars:    root.querySelector('[data-avg-stars] [data-stars-fill]'),
    reviewCount: root.querySelector('[data-review-count]'),
    chips:       root.querySelector('[data-keyword-chips]'),
    search:      root.querySelector('[data-search-input]'),
    clear:       root.querySelector('[data-clear-filters]'),
    range:       root.querySelector('[data-range-text]'),
    sort:        root.querySelector('[data-sort-select]'),
    list:        root.querySelector('[data-reviews-list]'),
    prev:        root.querySelector('[data-prev-page]'),
    next:        root.querySelector('[data-next-page]'),
    modal:       root.querySelector('[data-form-modal]'),
    form:        root.querySelector('[data-review-form]'),
    formError:   root.querySelector('[data-form-error]'),
    formSubmit:  root.querySelector('[data-form-submit]'),
    formSuccess: root.querySelector('[data-form-success]'),
    ratingInput: root.querySelector('[data-rating-input]'),
    reportModal:   root.querySelector('[data-report-modal]'),
    reportForm:    root.querySelector('[data-report-form]'),
    reportError:   root.querySelector('[data-report-error]'),
    reportSubmit:  root.querySelector('[data-report-submit]'),
    reportSuccess: root.querySelector('[data-report-success]'),
  };
  const reviewTemplate = root.querySelector('[data-review-template]');
  const chipTemplate   = root.querySelector('[data-chip-template]');
  const photoTemplate  = root.querySelector('[data-photo-template]');

  const state = {
    page: 1,
    sort: 'newest',
    filters: { rating: '', size_fit: '', body_type: '', height: '', keyword: '', search: '' },
    facetsLoaded: false,
    formOpenedAt: 0,
    selectedRating: 0,
    reportingReview: null,
  };

  const getStableId = (key) => {
    let id = localStorage.getItem(key);
    if (!id) {
      id = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(key, id);
    }
    return id;
  };
  const voterId = getStableId('ra_reviews_voter');
  const myVotes = JSON.parse(localStorage.getItem('ra_reviews_votes') || '{}');
  const saveMyVotes = () => localStorage.setItem('ra_reviews_votes', JSON.stringify(myVotes));

  const show = (el) => el?.classList.remove('hidden');
  const hide = (el) => el?.classList.add('hidden');

  // `overflow: hidden` alone doesn't stop iOS Safari's rubber-band scroll, so pin body in place instead.
  let modalScrollY = 0;
  const lockBodyScroll = () => {
    modalScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${modalScrollY}px`;
    document.body.style.width = '100%';
    document.body.classList.add('product-reviews-modal-open');
  };
  const unlockBodyScroll = () => {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.classList.remove('product-reviews-modal-open');
    window.scrollTo(0, modalScrollY);
  };

  const buildQuery = () => {
    const params = new URLSearchParams({
      product_external_id: productId,
      page: String(state.page),
      per_page: perPage || '5',
      sort: state.sort,
    });
    for (const [key, value] of Object.entries(state.filters)) {
      if (value) params.set(key, value);
    }
    return params.toString();
  };

  const setStars = (fillEl, rating) => {
    fillEl.style.setProperty('--stars-fill', `${(rating / 5) * 100}%`);
  };

  const renderAggregates = (data) => {
    const agg = data.aggregates;
    els.avgRating.textContent = agg ? agg.average_rating.toFixed(1) : '0.0';
    els.reviewCount.textContent = agg ? agg.review_count : 0;
    if (agg) setStars(els.avgStars, agg.average_rating);

    let pos = agg?.size_fit?.average_position;
    if (samplesEnabled && (pos === null || pos === undefined)) pos = 0.4;
    const mappedPos = pos !== null && pos !== undefined ? 1 / 6 + pos * (2 / 3) : null;
    for (const fitBlock of document.querySelectorAll('[data-fit-average]')) {
      if (mappedPos !== null) {
        fitBlock.querySelector('[data-fit-dot]').style.setProperty('--fit-position', `${mappedPos * 100}%`);
        show(fitBlock);
      } else {
        hide(fitBlock);
      }
    }
  };

  const renderFacets = (data) => {
    if (state.facetsLoaded) return;
    state.facetsLoaded = true;

    els.chips.textContent = '';
    for (const kw of data.facets?.keywords || []) {
      const chip = chipTemplate.content.cloneNode(true).querySelector('[data-slot="chip"]');
      chip.textContent = kw.label;
      chip.dataset.keyword = kw.value;
      chip.addEventListener('click', () => {
        const active = state.filters.keyword === kw.value;
        state.filters.keyword = active ? '' : kw.value;
        state.page = 1;
        for (const c of els.chips.children) c.classList.remove('is-active');
        if (!active) chip.classList.add('is-active');
        load();
      });
      els.chips.appendChild(chip);
    }

    for (const facetName of ['size_fit', 'body_type', 'height']) {
      const select = root.querySelector(`[data-facet-select="${facetName}"]`);
      const source = facetName === 'size_fit'
        ? (data.aggregates?.size_fit?.distribution || [])
        : (data.facets?.[facetName] || []);
      if (!select || !source.length) continue;
      for (const { value, count } of source) {
        select.add(new Option(`${value} (${count})`, value));
      }
      show(select.closest('[data-dropdown]'));
      dropdownRefresh.get(select)?.();
    }
  };

  const renderCard = (review) => {
    const card = reviewTemplate.content.cloneNode(true);
    const slot = (name) => card.querySelector(`[data-slot="${name}"]`);

    slot('name').textContent = review.reviewer_name;
    if (review._sample) show(slot('sample-badge'));
    if (review.verified) show(slot('verified'));
    if (review.size_fit) {
      slot('fit-value').textContent = review.size_fit;
      show(slot('fit-row'));
    }
    if (review.height) {
      slot('height').textContent = review.height;
      show(slot('height-row'));
    }
    if (review.body_type) {
      slot('body_type').textContent = review.body_type;
      show(slot('body-type-row'));
    }

    setStars(slot('stars-fill'), review.rating);
    if (review.title) {
      slot('title').textContent = review.title;
      show(slot('title'));
    }
    slot('body').textContent = review.body;

    if (review.pictures?.length && photoTemplate) {
      const photosWrap = slot('photos');
      for (const pic of review.pictures) {
        const src = pic.compact || pic.small || pic.original;
        if (!src) continue;
        const img = photoTemplate.content.cloneNode(true).querySelector('img');
        img.src = src;
        img.alt = `Photo from ${review.reviewer_name}'s review`;
        photosWrap.appendChild(img);
      }
      if (photosWrap.children.length) show(photosWrap);
    }

    const upBtn = slot('vote-up');
    const downBtn = slot('vote-down');
    slot('up-count').textContent = review.helpful_up;
    slot('down-count').textContent = review.helpful_down;
    if (review._sample) {
      upBtn.disabled = true;
      downBtn.disabled = true;
      slot('report').disabled = true;
      return card;
    }
    const mine = myVotes[review.id];
    if (mine === 'up') upBtn.classList.add('is-active');
    if (mine === 'down') downBtn.classList.add('is-active');

    const upCount = slot('up-count');
    const downCount = slot('down-count');
    const applyVoteClasses = () => {
      upBtn.classList.toggle('is-active', myVotes[review.id] === 'up');
      downBtn.classList.toggle('is-active', myVotes[review.id] === 'down');
    };

    // Optimistic UI: flip counts/state instantly, reconcile or roll back after the server responds.
    const vote = async (direction) => {
      const previous = myVotes[review.id] || 'clear';
      const next = previous === direction ? 'clear' : direction;
      const before = {
        up: Number(upCount.textContent) || 0,
        down: Number(downCount.textContent) || 0,
        vote: myVotes[review.id],
      };

      const optimistic = { up: before.up, down: before.down };
      if (previous !== 'clear') optimistic[previous] -= 1;
      if (next !== 'clear') optimistic[next] += 1;
      upCount.textContent = Math.max(0, optimistic.up);
      downCount.textContent = Math.max(0, optimistic.down);
      if (next === 'clear') delete myVotes[review.id];
      else myVotes[review.id] = next;
      applyVoteClasses();

      try {
        const res = await fetch(`${apiUrl}/reviews/${review.id}/vote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ direction: next, voter_id: voterId, product_external_id: Number(productId) }),
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const counts = await res.json();
        upCount.textContent = counts.helpful_up;
        downCount.textContent = counts.helpful_down;
        saveMyVotes();
      } catch {
        upCount.textContent = before.up;
        downCount.textContent = before.down;
        if (before.vote) myVotes[review.id] = before.vote;
        else delete myVotes[review.id];
        applyVoteClasses();
      }
    };
    upBtn.addEventListener('click', () => vote('up'));
    downBtn.addEventListener('click', () => vote('down'));

    const reportBtn = slot('report');
    reportBtn.addEventListener('click', () => {
      state.reportingReview = { id: review.id, button: reportBtn };
      els.reportForm.reset();
      hide(els.reportError);
      show(els.reportModal);
      show(els.reportForm);
      hide(els.reportSuccess);
      lockBodyScroll();
    });

    return card;
  };

  const render = (data) => {
    renderAggregates(data);
    renderFacets(data);

    els.list.textContent = '';
    if (samplesEnabled && data.page === 1) {
      for (const sample of SAMPLE_REVIEWS) els.list.appendChild(renderCard(sample));
    }
    for (const review of data.reviews) els.list.appendChild(renderCard(review));

    const start = (data.page - 1) * data.per_page + 1;
    const end = Math.min(data.page * data.per_page, data.total_filtered);
    els.range.textContent = data.total_filtered
      ? `${start}-${end} of ${data.total_filtered} Reviews`
      : '0 Reviews';

    els.prev.disabled = data.page <= 1;
    els.next.disabled = !data.has_next_page;

    const hasFilters = Object.values(state.filters).some(Boolean);
    els.clear.classList.toggle('hidden', !hasFilters);

    const noReviewsAtAll = !samplesEnabled && !data.total_filtered && !hasFilters && !data.aggregates?.review_count;
    if (noReviewsAtAll) {
      hide(els.content);
      show(els.empty);
    } else {
      show(els.content);
      hide(els.empty);
    }

    const noMatch = hasFilters && !data.total_filtered && !noReviewsAtAll;
    els.noMatch?.classList.toggle('hidden', !noMatch);
  };

  // Full spinner only on first load; refetches just dim the list in place.
  let firstLoad = true;
  let activeReviewsController = null;
  const load = async () => {
    hide(els.error);
    if (firstLoad) show(els.loading);
    else els.list.classList.add('is-loading');

    // Cancel any in-flight fetch so a rapid filter/sort/search change always wins.
    activeReviewsController?.abort();
    const controller = new AbortController();
    activeReviewsController = controller;

    try {
      const res = await fetch(`${apiUrl}/reviews?${buildQuery()}`, { signal: controller.signal });
      if (!res.ok) throw new Error(`status ${res.status}`);
      render(await res.json());
      firstLoad = false;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      hide(els.content);
      show(els.error);
    } finally {
      if (activeReviewsController === controller) {
        activeReviewsController = null;
        hide(els.loading);
        els.list.classList.remove('is-loading');
      }
    }
  };

  // Each dropdown is a trigger+listbox mirroring a hidden <select>, since native <select> popups can't be styled cross-browser.
  const dropdownRefresh = new Map();

  for (const wrapper of root.querySelectorAll('[data-dropdown]')) {
    const trigger = wrapper.querySelector('[data-dropdown-trigger]');
    const label = wrapper.querySelector('[data-dropdown-label]');
    const panel = wrapper.querySelector('[data-dropdown-panel]');
    const select = wrapper.querySelector('select');

    const closePanel = () => {
      hide(panel);
      trigger.setAttribute('aria-expanded', 'false');
    };

    const refresh = () => {
      const selected = select.options[select.selectedIndex];
      label.textContent = selected ? selected.text : '';
      panel.textContent = '';
      for (const option of select.options) {
        const item = document.createElement('li');
        item.className = 'product-reviews__dropdown-option';
        item.textContent = option.text;
        item.setAttribute('role', 'option');
        if (option.selected) {
          item.classList.add('is-active');
          item.setAttribute('aria-selected', 'true');
        }
        item.addEventListener('click', () => {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          refresh();
          closePanel();
        });
        panel.appendChild(item);
      }
    };
    dropdownRefresh.set(select, refresh);
    refresh();

    trigger.addEventListener('click', () => {
      if (panel.classList.contains('hidden')) {
        show(panel);
        trigger.setAttribute('aria-expanded', 'true');
      } else {
        closePanel();
      }
    });
  }

  document.addEventListener('click', (event) => {
    for (const wrapper of root.querySelectorAll('[data-dropdown]')) {
      if (wrapper.contains(event.target)) continue;
      wrapper.querySelector('[data-dropdown-panel]')?.classList.add('hidden');
      wrapper.querySelector('[data-dropdown-trigger]')?.setAttribute('aria-expanded', 'false');
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    for (const wrapper of root.querySelectorAll('[data-dropdown]')) {
      wrapper.querySelector('[data-dropdown-panel]')?.classList.add('hidden');
      wrapper.querySelector('[data-dropdown-trigger]')?.setAttribute('aria-expanded', 'false');
    }
  });


  for (const select of root.querySelectorAll('[data-filter-select]')) {
    select.addEventListener('change', () => {
      state.filters[select.dataset.filterSelect] = select.value;
      state.page = 1;
      load();
    });
  }

  els.sort.addEventListener('change', () => {
    state.sort = els.sort.value;
    state.page = 1;
    load();
  });

  let searchTimer;
  els.search.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      state.filters.search = els.search.value.trim();
      state.page = 1;
      load();
    }, 400);
  });

  els.clear.addEventListener('click', () => {
    state.filters = { rating: '', size_fit: '', body_type: '', height: '', keyword: '', search: '' };
    state.page = 1;
    els.search.value = '';
    for (const select of root.querySelectorAll('[data-filter-select]')) {
      select.value = '';
      dropdownRefresh.get(select)?.();
    }
    for (const c of els.chips.children) c.classList.remove('is-active');
    load();
  });

  const goToPage = async (delta) => {
    state.page += delta;
    await load();
    root.querySelector('.product-reviews__toolbar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  els.prev.addEventListener('click', () => goToPage(-1));
  els.next.addEventListener('click', () => goToPage(1));


  for (const opener of root.querySelectorAll('[data-open-form]')) {
    opener.addEventListener('click', () => {
      state.formOpenedAt = Date.now();
      show(els.modal);
      show(els.form);
      hide(els.formSuccess);
      lockBodyScroll();
    });
  }

  for (const closer of root.querySelectorAll('[data-close-form]')) {
    closer.addEventListener('click', () => {
      hide(els.modal);
      unlockBodyScroll();
    });
  }


  for (const closer of root.querySelectorAll('[data-close-report]')) {
    closer.addEventListener('click', () => {
      hide(els.reportModal);
      unlockBodyScroll();
    });
  }

  els.reportForm?.addEventListener('submit', async (event) => {
    event.preventDefault();
    hide(els.reportError);

    const reason = String(new FormData(els.reportForm).get('reason') || '').trim();
    if (!reason) {
      els.reportError.textContent = "Please tell us why you're reporting this review.";
      show(els.reportError);
      return;
    }

    els.reportSubmit.disabled = true;
    els.reportSubmit.textContent = 'Submitting…';

    try {
      await fetch(`${apiUrl}/reviews/${state.reportingReview.id}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_external_id: Number(productId),
          reporter_id: voterId,
          reason: reason.slice(0, 300),
        }),
      });
      if (state.reportingReview?.button) {
        state.reportingReview.button.textContent = 'Reported';
        state.reportingReview.button.disabled = true;
      }
      hide(els.reportForm);
      show(els.reportSuccess);
    } catch {
      els.reportError.textContent = 'Something went wrong. Please try again.';
      show(els.reportError);
    } finally {
      els.reportSubmit.disabled = false;
      els.reportSubmit.textContent = 'Submit Report';
    }
  });

  for (const star of els.ratingInput.querySelectorAll('[data-rating-value]')) {
    star.addEventListener('click', () => {
      state.selectedRating = Number(star.dataset.ratingValue);
      for (const s of els.ratingInput.querySelectorAll('[data-rating-value]')) {
        s.classList.toggle('is-active', Number(s.dataset.ratingValue) <= state.selectedRating);
      }
    });
  }

  const uploadPhoto = async (file) => {
    const presignRes = await fetch(`${apiUrl}/uploads/presign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content_type: file.type }),
    });
    if (!presignRes.ok) throw new Error('presign_failed');
    const { upload, public_url: publicUrl } = await presignRes.json();

    const formData = new FormData();
    for (const [key, value] of Object.entries(upload.fields)) formData.append(key, value);
    formData.append('file', file);
    const uploadRes = await fetch(upload.url, { method: 'POST', body: formData });
    if (!uploadRes.ok) throw new Error('upload_failed');
    return publicUrl;
  };

  els.form.addEventListener('submit', async (event) => {
    event.preventDefault();
    hide(els.formError);

    const formData = new FormData(els.form);
    const showFormError = (message) => {
      els.formError.textContent = message;
      show(els.formError);
    };

    if (!state.selectedRating) return showFormError('Please select a star rating.');
    const body = String(formData.get('body') || '').trim();
    if (body.length < 10) return showFormError('Please write at least 10 characters.');

    const files = [...(root.querySelector('#pr-form-photos')?.files || [])];
    if (files.length > 5) return showFormError('Up to 5 photos allowed.');
    if (files.some((f) => f.size > 8 * 1024 * 1024)) return showFormError('Each photo must be under 8 MB.');

    els.formSubmit.disabled = true;
    els.formSubmit.textContent = 'Submitting…';

    try {
      const pictureUrls = await Promise.all(files.map(uploadPhoto));

      const payload = {
        product_external_id: Number(productId),
        name: formData.get('name'),
        email: formData.get('email'),
        rating: state.selectedRating,
        title: formData.get('title'),
        body,
        size_fit: formData.get('size_fit') || undefined,
        height: formData.get('height') || undefined,
        body_type: formData.get('body_type') || undefined,
        picture_urls: pictureUrls,
        website: formData.get('website'),
        form_ms: Date.now() - state.formOpenedAt,
      };

      const res = await fetch(`${apiUrl}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.status === 429) return showFormError('You have already submitted a review for this product recently.');
      if (res.status === 422) {
        const err = await res.json().catch(() => ({}));
        const firstField = err.fields ? Object.values(err.fields)[0] : null;
        return showFormError(firstField || 'Please check your review and try again.');
      }
      if (!res.ok) throw new Error(`status ${res.status}`);

      els.form.reset();
      state.selectedRating = 0;
      for (const s of els.ratingInput.querySelectorAll('[data-rating-value]')) s.classList.remove('is-active');
      hide(els.form);
      show(els.formSuccess);
    } catch {
      showFormError('Something went wrong. Please try again.');
    } finally {
      els.formSubmit.disabled = false;
      els.formSubmit.textContent = 'Submit Review';
    }
  });

  load();
});
